import { useMemo, useRef, useState } from 'react'

import * as yup from 'yup'
import { Form, Formik, FormikProps } from 'formik'

import {
    Avatar,
    Box,
    Button,
    FormHelperText,
    makeStyles,
    Typography,
    Switch,
    Tooltip,
    Divider
} from '@material-ui/core'

import PeopleAltIcon from '@material-ui/icons/LockOutlined'
import PublicIcon from '@material-ui/icons/PublicOutlined'

import { ReactComponent as PublicComments } from 'assets/icons/publicComments.svg'
import { ReactComponent as CommentsOff } from 'assets/icons/commentsOff.svg'
import { ReactComponent as TeamIcon } from 'assets/icons/team-icon.svg'

import {
    ConnectedAutocomplete,
    ConnectedFreeSoloInput,
    ConnectedInput,
    SpacedGroup,
    FormObserver
} from 'UI/Components'

import {
    useDataMutation,
    useAuth,
    useOrganization,
    ORG_SHARE_OPTIONS,
    useServiceUsage,
    useOrgSpaces,
    useOrgFeatureFlags,
    COMMENTS_STATUS_OPTIONS
} from 'hooks'

import { uiId, privateDomains, option, OptionType, logToAnalytics } from 'modules'
import { FancyDropdown } from './FancyDropdown'

const useStyles = makeStyles(({ spacing, typography }) => ({
    customTextField: {
        '& input::placeholder': {
            fontSize: '12px'
        }
    },
    spacesAutocomplete: {
        '& > h3': { fontSize: '12px', color: 'rgba(0, 0, 0, 0.54)' }
    },
    secondaryTitle: {
        fontSize: '11px',
        lineHeight: 'normal',
        color: 'rgba(0, 0, 0, 0.54)'
    },
    spaceOption: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '85px',
        '&[aria-selected="true"]': {
            opacity: '0.38'
        }
    },
    spaceLogo: {
        width: '32px',
        height: '32px',
        marginRight: spacing(2),
        fontSize: typography.pxToRem(13)
    },
    defaultSpaces: {
        marginBottom: spacing(1)
    },
    title: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#090C10',
        marginBottom: '8px'
    },
    caption: {
        fontSize: '14px',
        fontWeight: 400,
        color: '#090C10'
    }
}))

type FormValuesType = {
    name: string
    id: string
    useDefaultCoverColors: boolean
    domains: Array<string>
    defaultSpaces: Array<OptionType>
    defaultSharingSettings: keyof typeof ORG_SHARE_OPTIONS
    comments: string
}

type OnChangeType = {
    prev: FormValuesType
    next: FormValuesType
}

type SubmitValuesType = Omit<FormValuesType, 'defaultSpaces'> & {
    defaultSpaces: Array<string>
    comments: string
}

type Props = {
    initialValues: SubmitValuesType
    onSubmit: (fieldsData: SubmitValuesType) => void
    disabled?: boolean
}

const getPbPublicityOptions = (orgName: string, privateAccount: boolean) =>
    [
        {
            icon: (
                <Avatar style={{ backgroundColor: '#F5F5F5', color: '#090C10' }}>
                    <PublicIcon />
                </Avatar>
            ),
            value: ORG_SHARE_OPTIONS.public,
            label: 'Public',
            caption: 'Anyone with the link can view'
        },
        ...(privateAccount
            ? []
            : [
                  {
                      icon: (
                          <Avatar style={{ backgroundColor: '#F5F5F5', color: '#090C10' }}>
                              <TeamIcon />
                          </Avatar>
                      ),
                      value: ORG_SHARE_OPTIONS.anyoneInOrg,
                      label: `Anyone in ${orgName}`,
                      caption: 'All members in this account can view'
                  }
              ]),
        {
            icon: (
                <Avatar style={{ backgroundColor: '#F5F5F5', color: '#090C10' }}>
                    <PeopleAltIcon />
                </Avatar>
            ),
            value: ORG_SHARE_OPTIONS.private,
            label: `Private`,
            caption: 'Only members and spaces added can open this link'
        }
    ].sort((a, b) => (a?.label?.toLowerCase() > b?.label?.toLowerCase() ? -1 : 1))

const getCommentsOptions = (orgName: string, privateAccount: boolean) => [
    {
        icon: <PublicComments />,
        value: COMMENTS_STATUS_OPTIONS.on,
        label: 'Public comments',
        caption: 'Anyone can add, view, and reply to comments (default)'
    },
    ...(privateAccount
        ? []
        : [
              {
                  icon: (
                      <Avatar style={{ backgroundColor: '#F5F5F5', color: '#1C1B1F' }}>
                          <PeopleAltIcon />
                      </Avatar>
                  ),
                  value: COMMENTS_STATUS_OPTIONS.anyoneInOrg,
                  label: 'Restricted',
                  caption: `Anyone in ${orgName} can add, view, and reply to comments`
              }
          ]),
    {
        icon: <CommentsOff />,
        value: COMMENTS_STATUS_OPTIONS.off,
        caption: 'Turn off comments',
        label: `Comments off`
    }
]

export const OrganizationForm = ({ initialValues, onSubmit, disabled }: Props) => {
    const formRef = useRef<FormikProps<FormValuesType>>(null)
    const classes = useStyles()

    const { isPrivateAccount, uid } = useAuth()
    const { name } = useOrganization()
    const { hasPrivacyControl } = useServiceUsage()

    const [existingDomains, setExistingDomains] = useState<FormValuesType['domains']>([])
    const { orgSpacesData, isOrgSpacesLoading } = useOrgSpaces()
    const { showDefaultCoverColors, enableComments } = useOrgFeatureFlags()

    const $checkDomain = useDataMutation<unknown, { exists: boolean }, Error>(
        '/c/v1/checkDomain',
        'POST'
    )

    const onChange = async ({ prev, next }: OnChangeType) => {
        const prevDomains = prev.domains
        const nextDomains = next.domains

        if (!nextDomains || !prevDomains) return null

        if (nextDomains.length < prevDomains.length) {
            const deletedDomain = prevDomains.filter(value => !nextDomains.includes(value)).join()
            return setExistingDomains(prevDomains =>
                prevDomains.filter(domain => domain !== deletedDomain)
            )
        }

        const enteredDomain = nextDomains
            .filter(value => !prevDomains.includes(value) && !initialValues.domains.includes(value))
            .join()

        const isExists = enteredDomain && (await checkDomain(enteredDomain))

        if (isExists) {
            setExistingDomains(prevDomains => prevDomains.concat(enteredDomain))
        }
    }

    const checkDomain = (domain: string) => {
        return $checkDomain
            .mutate({
                domain
            })
            .then(obj => obj!.exists)
    }

    const onDone = (values: FormValuesType) => {
        if (!values) return

        onSubmit({
            ...values,
            domains: values.domains.map(it => it.toLowerCase()),
            defaultSpaces: values.defaultSpaces?.map(({ value }) => value) || [],
            comments: values.comments
        })
    }

    const spaceOptions = useMemo(
        () =>
            orgSpacesData
                .filter(({ isPrivate }) => !isPrivate)
                .sort((a, b) => (a?.name?.toLowerCase() > b?.name?.toLowerCase() ? 1 : -1))
                .map(({ name, id }) => option(id, name)),
        [orgSpacesData]
    )

    return (
        <Box mr={3} mt={3}>
            <Formik
                innerRef={formRef}
                enableReinitialize={true}
                onSubmit={onDone}
                initialValues={{
                    ...initialValues,
                    defaultSpaces: isOrgSpacesLoading
                        ? []
                        : initialValues.defaultSpaces.map(spaceId => {
                              const space = orgSpacesData.find(({ id }) => spaceId === id)

                              return option(spaceId, space?.name || '')
                          })
                }}
                validationSchema={yup.object().shape({
                    name: yup.string().trim().nullable().required('Field is required'),
                    ...(!isPrivateAccount && {
                        domains: yup
                            .array()
                            .min(1, 'Field is required')
                            .of(
                                yup
                                    .string()
                                    .trim()
                                    .test('isAllowedDomain', '', (value, { createError }) => {
                                        const invalid = privateDomains.includes(
                                            value?.toLowerCase() || ''
                                        )

                                        if (invalid) {
                                            return createError({
                                                message: `${value} can't be added`,
                                                path: 'domains'
                                            })
                                        }

                                        return true
                                    })
                            )
                    })
                })}
            >
                {({ values, dirty }) => (
                    <Form>
                        <FormObserver<FormValuesType> onChange={onChange} />
                        <SpacedGroup flexDirection="column">
                            <ConnectedInput
                                fullWidth
                                label="Account Name"
                                id={uiId('edit-org-name-input')}
                                name="name"
                                placeholder="Enter account name"
                                disableUnderline
                                InputLabelProps={{
                                    style: {
                                        fontSize: '18px',
                                        fontWeight: 500,
                                        color: '#090C10'
                                    }
                                }}
                            />

                            <Divider light={true} />

                            {!isPrivateAccount && (
                                <Box>
                                    <ConnectedFreeSoloInput
                                        fullWidth
                                        label="Account Email Domains"
                                        name="domains"
                                        placeholder={
                                            !values?.domains?.length ? 'Enter domains' : ''
                                        }
                                        id={uiId('edit-org-domains-input')}
                                        disableUnderline
                                        InputLabelProps={{
                                            style: {
                                                fontSize: '18px',
                                                fontWeight: 500,
                                                color: '#090C10'
                                            }
                                        }}
                                    />
                                    {existingDomains.length > 0 && (
                                        <FormHelperText>
                                            {`${existingDomains.join(', ')} ${
                                                existingDomains.length > 1 ? 'are' : 'is'
                                            } already in use`}
                                        </FormHelperText>
                                    )}
                                </Box>
                            )}

                            <Divider light={true} />

                            <Box className={classes.spacesAutocomplete}>
                                <Typography className={classes.title}>
                                    Account Default Spaces
                                </Typography>

                                <Typography className={classes.caption}>
                                    Spaces that members will automatically join
                                </Typography>
                                <Box mt={1.2} />
                                <ConnectedAutocomplete
                                    classes={{ option: classes.spaceOption }}
                                    rootClass={classes.customTextField}
                                    fullWidth
                                    multiple
                                    placeholder="Select spaces"
                                    variant="standard"
                                    name="defaultSpaces"
                                    filterSelectedOptions={false}
                                    disableUnderline
                                    options={spaceOptions}
                                    renderOption={({ label, value: spaceId }) => {
                                        const space = orgSpacesData.find(({ id }) => id === spaceId)

                                        if (!space) return

                                        const { logo, color } = space

                                        return (
                                            <>
                                                <Avatar
                                                    src={logo}
                                                    variant={logo ? 'square' : 'circular'}
                                                    className={classes.spaceLogo}
                                                    style={{
                                                        background: logo
                                                            ? 'none'
                                                            : color || '#1976d2'
                                                    }}
                                                >
                                                    {label.slice(0, 2).toUpperCase()}
                                                </Avatar>

                                                <Typography variant="body2">
                                                    <b>{label}</b>
                                                </Typography>
                                            </>
                                        )
                                    }}
                                />
                            </Box>

                            <Divider light={true} />

                            <Box style={{ opacity: hasPrivacyControl ? 1 : 0.6 }}>
                                <Typography className={classes.title}>Sharing settings</Typography>
                                <Typography className={classes.caption}>
                                    Default sharing settings for all guides in your account.
                                </Typography>
                                <Box mt={1.2} />
                                <Box>
                                    <Tooltip
                                        arrow
                                        title={
                                            hasPrivacyControl
                                                ? ''
                                                : 'Available on the Business Plan'
                                        }
                                        placement="right"
                                    >
                                        <Box mt={1}>
                                            <FancyDropdown
                                                options={getPbPublicityOptions(
                                                    name,
                                                    isPrivateAccount
                                                )}
                                                name="defaultSharingSettings"
                                                disabled={!hasPrivacyControl}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Box>

                            {enableComments && (
                                <>
                                    <Divider light={true} />
                                    <Box>
                                        <Typography className={classes.title}>
                                            Comment settings
                                        </Typography>

                                        <Typography className={classes.caption}>
                                            Default comment settings for all guides in your account.
                                            Creators can override the settings if needed.
                                        </Typography>

                                        <Box mt={1}>
                                            <FancyDropdown
                                                options={getCommentsOptions(name, isPrivateAccount)}
                                                name="comments"
                                                onChange={value => {
                                                    logToAnalytics('Comments', {
                                                        eventName: `Account comment settings - ${value}`,
                                                        user: uid
                                                    })
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {showDefaultCoverColors && (
                                <Box>
                                    <Box my={2}>
                                        <Divider light={true} />
                                    </Box>
                                    <Typography className={classes.title}>
                                        Video Cover Color
                                    </Typography>
                                    <SpacedGroup>
                                        <Typography
                                            className={classes.caption}
                                            style={{ minWidth: '250px' }}
                                        >
                                            Set cover color by default
                                        </Typography>
                                        <Switch
                                            checked={values.useDefaultCoverColors}
                                            color="primary"
                                        />
                                    </SpacedGroup>
                                </Box>
                            )}
                        </SpacedGroup>
                        <Box mt={4} />
                        <Button
                            size="medium"
                            type="submit"
                            id={uiId('edit-org-update-btn')}
                            disabled={disabled || existingDomains.length > 0 || !dirty}
                        >
                            <b>Update</b>
                        </Button>
                    </Form>
                )}
            </Formik>
        </Box>
    )
}
